// How the Blunders and Misses screens group the reader's mistakes, and the
// library's `mistake` filter that opens each group's games. One function per
// dimension, and `mistakeKeys` naming every group one mistake belongs to as
// `<kind>-<dimension>-<value>` (e.g. `blunder-motif-fork`,
// `blunder-motif-fork/knight`, `miss-clock-under1`). The cards count a game
// in a group when one of its mistakes has the key, and the store's
// `resolveFilter` keeps exactly those games (`gameMistakeKeys`), so a bar's
// number is the library's count.
//
// The `motif` dimension (2026-10-12) names a mistake's motif (`motif.ts`) by
// family or by family and variation: `blunder-motif-pin`,
// `blunder-motif-pin/queen`, so a family's group holds every variation's
// games. Its value is not in the fact: it comes from the `mistakes.motifs`
// service, which the caller hands in as a `MotifOf` (memoised per fact by
// `memoisedMotifs`); without one the motif keys are left out. It replaced
// the `pattern` dimension (mate, hanging, fork, tactic, positional), which
// the family generalises; `patternOf` stays in `classify.ts` for the ground
// cause and the classifier's sentence.
import { MISTAKE_STANDINGS, type GameFacts, type MistakeFact, type MistakeStanding, type MoveKind } from '@chess/api-model';
import { MATERIALS, SEVERITIES, SEVERITY_LABEL, severityOf, materialOf, type Material, type Severity } from './classify.ts';
import { MOTIF_IDS, motifId, motifLabel, parseMotifId, type Motif, type MotifOf } from './motif.ts';

export const MOVE_KINDS = ['blunder', 'miss'] as const satisfies readonly MoveKind[];

/** The ways a mistake is grouped. */
export type MistakeDimension = 'motif' | 'severity' | 'standing' | 'clock' | 'material' | 'week';
export const MISTAKE_DIMENSIONS = ['motif', 'severity', 'standing', 'clock', 'material', 'week'] as const satisfies readonly MistakeDimension[];

/** A motif key's value: a family (`pin`) or a family and its variation (`pin/queen`), as `motifId` spells them. */
export type MotifKeyValue = string;

/** The reader's clock before the move: under a minute, one to three, three or more. */
export type ClockBucket = 'under1' | '1to3' | '3plus';
export const CLOCK_BUCKETS = ['under1', '1to3', '3plus'] as const satisfies readonly ClockBucket[];

/** A week of the 30 days: 3 the last seven days, 2 and 1 the weeks before, 0 the oldest, which takes the two days left over (days 22 to 30). */
export type MistakeWeek = 0 | 1 | 2 | 3;
export const MISTAKE_WEEKS = [0, 1, 2, 3] as const satisfies readonly MistakeWeek[];

/** Each dimension's values, as its keys spell them. */
export interface DimensionValue {
  motif: MotifKeyValue;
  severity: Severity;
  standing: MistakeStanding;
  clock: ClockBucket;
  material: Material;
  week: MistakeWeek;
}
export const DIMENSION_VALUES: { [D in MistakeDimension]: readonly DimensionValue[D][] } = {
  motif: MOTIF_IDS,
  severity: SEVERITIES,
  standing: MISTAKE_STANDINGS,
  clock: CLOCK_BUCKETS,
  material: MATERIALS,
  week: MISTAKE_WEEKS,
};

/** One group of one kind of mistake: `<kind>-<dimension>-<value>`. */
export type MistakeKey = `${MoveKind}-${MistakeDimension}-${string}`;

export function mistakeKey<D extends MistakeDimension>(kind: MoveKind, dimension: D, value: DimensionValue[D]): MistakeKey {
  return `${kind}-${dimension}-${value}`;
}

/** Every key there is, kind by kind, dimension by dimension, in the values' order. */
export const MISTAKE_KEYS: readonly MistakeKey[] = MOVE_KINDS.flatMap((kind) =>
  MISTAKE_DIMENSIONS.flatMap((d) => (DIMENSION_VALUES[d] as readonly (string | number)[]).map((v) => `${kind}-${d}-${v}` as MistakeKey)),
);

/** A key's parts; undefined for a key that is not one of `MISTAKE_KEYS`. */
export function parseMistakeKey(key: string): { kind: MoveKind; dimension: MistakeDimension; value: string } | undefined {
  if (!(MISTAKE_KEYS as readonly string[]).includes(key)) return undefined;
  const [kind, dimension, ...rest] = key.split('-');
  return { kind: kind as MoveKind, dimension: dimension as MistakeDimension, value: rest.join('-') };
}

/** Seconds on the clock under which a mistake was made under a minute (`TIME_SLIP_SECONDS`'s line) and at or over which with three or more. */
export const CLOCK_UNDER1_SECONDS = 60;
export const CLOCK_3PLUS_SECONDS = 180;

/** How the reader stood before the move, by the Winning setting (`MistakeFact.standing`); undefined in facts that lack it. */
export function standingOf(m: Pick<MistakeFact, 'standing'>): MistakeStanding | undefined {
  return m.standing;
}

/** The reader's clock before the move, bucketed; undefined without a clock. */
export function clockBucketOf(m: Pick<MistakeFact, 'clock'>): ClockBucket | undefined {
  if (m.clock === undefined) return undefined;
  return m.clock < CLOCK_UNDER1_SECONDS ? 'under1' : m.clock < CLOCK_3PLUS_SECONDS ? '1to3' : '3plus';
}

const DAY = 24 * 60 * 60 * 1000;
const WINDOW_DAYS = 30;

/**
 * The week of the 30 days ending `now` a game was played in: 3 for the
 * last seven days, then 2 and 1, and 0 for the oldest, which takes the days
 * past four weeks (as the Blunders trend did until 2026-10-05). Undefined
 * outside the window (the store's: after `now` − 30 days, not after `now`).
 */
export function weekOf(playedAt: number, now: number): MistakeWeek | undefined {
  if (!(playedAt > now - WINDOW_DAYS * DAY && playedAt <= now)) return undefined;
  return (3 - Math.min(3, Math.floor((now - playedAt) / (7 * DAY)))) as MistakeWeek;
}

/** The game a mistake's keys read: whose side the reader played, and when. */
export type KeyedGame = Pick<GameFacts, 'colour' | 'playedAt'>;

/**
 * Every group one mistake belongs to, as keys of `kind`; none when the
 * mistake is of the other kind. A dimension without a value (no clock, no
 * standing, outside the window) adds no key; the motif keys (`motifKeys`)
 * come first when `motifOf` is given, and are left out without it.
 */
export function mistakeKeys(kind: MoveKind, m: MistakeFact, game: KeyedGame, now: number, motifOf?: MotifOf): MistakeKey[] {
  if (m.kind !== kind) return [];
  const keys: MistakeKey[] = motifOf ? motifKeys(kind, motifOf(m, game.colour)) : [];
  keys.push(mistakeKey(kind, 'severity', severityOf(m.before, m.after)));
  const standing = standingOf(m);
  if (standing) keys.push(mistakeKey(kind, 'standing', standing));
  const clock = clockBucketOf(m);
  if (clock) keys.push(mistakeKey(kind, 'clock', clock));
  keys.push(mistakeKey(kind, 'material', materialOf(m)));
  const week = weekOf(game.playedAt, now);
  if (week !== undefined) keys.push(mistakeKey(kind, 'week', week));
  return keys;
}

/**
 * The motif keys of one mistake of `kind` whose motif the `mistakes.motifs`
 * service found: its family's key and, when it has one, its variation's
 * (`blunder-motif-pin`, `blunder-motif-pin/queen`), so a family's group
 * holds every variation's games.
 */
export function motifKeys(kind: MoveKind, motif: Motif): MistakeKey[] {
  const keys: MistakeKey[] = [mistakeKey(kind, 'motif', motif.family)];
  if (motif.variation) keys.push(mistakeKey(kind, 'motif', motifId(motif)));
  return keys;
}

/** The motif a motif key names; undefined for any other key. */
export function motifOfKey(key: string): Motif | undefined {
  const parts = parseMistakeKey(key);
  return parts?.dimension === 'motif' ? parseMotifId(parts.value) : undefined;
}

/** Every key any of a game's mistakes has, both kinds (the motif ones with a `motifOf`): the `mistake` filter keeps a game when this holds one of the chosen keys. */
export function gameMistakeKeys(facts: KeyedGame & Pick<GameFacts, 'mistakes'>, now: number, motifOf?: MotifOf): Set<MistakeKey> {
  const out = new Set<MistakeKey>();
  for (const m of facts.mistakes ?? []) for (const k of mistakeKeys(m.kind, m, facts, now, motifOf)) out.add(k);
  return out;
}

// Names, for the cards and the library's chips.

/** Each kind as a group of the filter's menu. */
export const MISTAKE_KIND_LABEL: Record<MoveKind, string> = { blunder: 'Blunders', miss: 'Misses' };
export const STANDING_BEFORE_LABEL: Record<MistakeStanding, string> = { winning: 'When winning', level: 'When level', losing: 'When losing' };
export const CLOCK_BUCKET_LABEL: Record<ClockBucket, string> = { under1: 'Under 1 min on the clock', '1to3': '1–3 min on the clock', '3plus': '3+ min on the clock' };
export const WEEK_LABEL: Record<MistakeWeek, string> = { 3: 'Last 7 days', 2: '8–14 days ago', 1: '15–21 days ago', 0: '22–30 days ago' };
/** A material key in words: what happened, where `MATERIAL_LABEL` (a card's bars) names only the piece. */
const MATERIAL_KEY_LABEL: Record<MoveKind, Record<Material, string>> = {
  blunder: { queen: 'Lost the queen', rook: 'Lost a rook', bishop: 'Lost a bishop', knight: 'Lost a knight', pawn: 'Lost a pawn', mate: 'Lost to a forced mate', none: 'No material lost' },
  miss: { queen: 'Missed winning the queen', rook: 'Missed winning a rook', bishop: 'Missed winning a bishop', knight: 'Missed winning a knight', pawn: 'Missed winning a pawn', mate: 'Missed a forced mate', none: 'No material missed' },
};

/** A key's value in words, within its kind: "Fork · by a knight", "Under 1 min on the clock"; undefined for an unknown key. */
export function mistakeKeyLabel(key: string): string | undefined {
  const parts = parseMistakeKey(key);
  if (!parts) return undefined;
  const { kind, dimension, value } = parts;
  switch (dimension) {
    case 'motif': {
      const motif = parseMotifId(value);
      return motif ? motifLabel(motif) : undefined;
    }
    case 'severity':
      return SEVERITY_LABEL[value as Severity];
    case 'standing':
      return STANDING_BEFORE_LABEL[value as MistakeStanding];
    case 'clock':
      return CLOCK_BUCKET_LABEL[value as ClockBucket];
    case 'material':
      return MATERIAL_KEY_LABEL[kind][value as Material];
    case 'week':
      return WEEK_LABEL[Number(value) as MistakeWeek];
  }
}
